import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Mail, MapPin, Quote } from "lucide-react";
import { tenantHref } from "@/lib/tenantPath";
import ContactForm from "@/components/site/ContactForm";
import { TemplateShell } from "./shared/TemplateShell";
import { MinimalHeader, MinimalFooter, MINIMAL_MAIN_CLASS } from "./chrome/MinimalChrome";
import { useSiteContent, useTestimonials, useBlogPosts, useServices } from "./shared/useSiteData";
import BlogGrid from "./shared/BlogGrid";
// Relative, not "@/": Hero.fillerText.test.js mounts this template with only a handful of
// "@/" modules virtual-mocked (no craco jest moduleNameMapper for "@/") — see Hero.jsx's
// own identical note.
import { hasText } from "../lib/siteContent";
import { formatQuote } from "../lib/testimonials";

/**
 * Minimal / Clean — Swiss minimalist light.
 * Nav pilule flottante centrée · hero typographique centré + image bas full-bleed ·
 * grilles symétriques à filets 1px · sections aérées (py-32) · boutons noirs pill.
 */
/**
 * The shared ContactForm in this theme's idiom: no boxes, 1px hairline underlines that go
 * solid black on focus, micro uppercase labels, black pill submit — the same vocabulary as
 * the services grid and the hero CTA above.
 */
const FORM_CLASSES = {
  grid: "grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7",
  fieldWide: "sm:col-span-2",
  label: "block text-[11px] uppercase tracking-[0.25em] text-black/40",
  input: "mt-2 w-full bg-transparent border-b border-black/15 py-2 outline-none focus:border-black transition-colors",
  textarea: "mt-2 w-full bg-transparent border-b border-black/15 py-2 outline-none focus:border-black transition-colors resize-none",
  hint: "mt-3 text-[11px] uppercase tracking-[0.2em] text-black/40",
  actions: "mt-10 flex items-center gap-5 flex-wrap",
  submit: "inline-flex items-center gap-2 rounded-full bg-black text-white px-8 py-4 font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-50",
  success: "text-sm text-black/60",
  error: "text-sm text-red-600",
};

export default function MinimalTemplate({ template, toggles = {} }) {
  const c = useSiteContent(toggles);
  const testimonials = useTestimonials();
  const posts = useBlogPosts();
  const services = useServices();
  const [active, setActive] = React.useState(0);
  const show = (k) => toggles?.[k] !== false;

  React.useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % Math.max(testimonials.length, 1)), 5000);
    return () => clearInterval(id);
  }, [testimonials.length]);

  return (
    <TemplateShell template={template} branding={c.branding}>
      <main data-testid="tpl-minimal" className={MINIMAL_MAIN_CLASS}>
        {/* The header is the theme's CHROME (chrome/MinimalChrome.jsx), the same component
            the interior pages render through ThemedPageLayout — never an inline copy. */}
        <MinimalHeader toggles={toggles} />

        {/* HERO — centré, typographique */}
        <section id="top" className="pt-44 pb-0 px-6" data-testid="hero-section">
          <div className="max-w-4xl mx-auto text-center">
            {/* BUG-210: no dictionary sample text backs this any more (lib/content.js) — same
                guard idiom as Hero.jsx's hero-eyebrow. */}
            {hasText(c.hero.eyebrow) && (
              <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="inline-block text-xs uppercase tracking-[0.3em] text-black/40 mb-8">{c.hero.eyebrow}</motion.span>
            )}
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="tpl-head text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight leading-[0.95]">
              {c.hero.titleLines.map((line, i) => <span key={i} className="block">{line}</span>)}
            </motion.h1>
            {c.hero.sub && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mt-8 text-lg md:text-xl text-black/55 max-w-2xl mx-auto leading-relaxed">{c.hero.sub}</motion.p>}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="mt-10 flex items-center justify-center gap-4 flex-wrap">
              <a href={tenantHref("/reserver")} data-testid="hero-primary-cta" className="inline-flex items-center gap-2 rounded-full bg-black text-white px-8 py-4 font-medium hover:-translate-y-0.5 transition-transform">{c.hero.cta} <ArrowRight size={16} /></a>
              <a href="#services" className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-medium hover:border-black transition-colors">{c.hero.ctaSecondary}</a>
            </motion.div>
            {/* BUG-196: an un-wrapped `flex` row could not drop a stat to a second
                line, so three items wider than the phone dragged the row (and the
                page, per BUG-197) sideways. `flex-wrap` is the fix. */}
            <div data-testid="hero-stats" className="mt-14 flex flex-wrap items-center justify-center gap-10">
              {c.hero.stats.map((s, i) => (
                <div key={i} className="text-center">
                  <div className="tpl-head text-3xl font-bold">{s.v}</div>
                  <div className="text-[11px] uppercase tracking-widest text-black/40 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }} className="mt-20 max-w-6xl mx-auto">
            <img src={c.images.heroBg} alt="" className="w-full h-[340px] md:h-[520px] object-cover rounded-md" data-testid="hero-portrait" />
          </motion.div>
        </section>

        {/* STORY — 50/50 sticky */}
        {show("story") && (
          <section id="story" className="py-28 md:py-36 px-6" data-testid="story-section">
            <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16">
              <div className="md:sticky md:top-32 self-start">
                <span className="text-xs uppercase tracking-[0.3em] text-black/40">{c.story.eyebrow}</span>
                <h2 className="tpl-head text-4xl md:text-5xl font-bold mt-5 leading-tight whitespace-pre-line">{c.story.title}</h2>
                <p className="mt-6 text-lg text-black/55 leading-relaxed">{c.story.lead}</p>
              </div>
              <div className="space-y-12">
                {c.story.chapters.map((ch, i) => (
                  <motion.div key={ch.n} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} data-testid={`story-chapter-${ch.n}`} className="border-t border-black/10 pt-6">
                    <div className="text-sm text-black/30 font-mono">{String(i + 1).padStart(2, "0")}</div>
                    <h3 className="tpl-head text-2xl font-semibold mt-2">{ch.title}</h3>
                    <p className="mt-3 text-black/55 leading-relaxed">{ch.body}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* APPROACH — the pillars as a hairline-ruled, numbered column set: the same
            typographic vocabulary as the story chapters above, so the section reads as a
            continuation of the argument rather than a second card grid. */}
        {show("approach") && (
          <section id="approach" className="py-28 md:py-36 px-6 border-t border-black/10" data-testid="approach-section">
            <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-16">
              <div className="md:col-span-1 md:sticky md:top-32 self-start">
                <span className="text-xs uppercase tracking-[0.3em] text-black/40">{c.approach.eyebrow}</span>
                <h2 className="tpl-head text-4xl md:text-5xl font-bold mt-5 leading-tight whitespace-pre-line">{c.approach.title}</h2>
              </div>
              <div className="md:col-span-2">
                <p className="text-lg text-black/55 leading-relaxed max-w-2xl">{c.approach.lead}</p>
                <div className="mt-14 grid sm:grid-cols-2 gap-x-12 gap-y-10">
                  {c.approach.pillars.map((p, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.06 }} data-testid={`approach-pillar-${i}`} className="border-t border-black/10 pt-6">
                      <div className="text-sm text-black/30 font-mono">{String(i + 1).padStart(2, "0")}</div>
                      <h3 className="tpl-head text-2xl font-semibold mt-2">{p.title}</h3>
                      <p className="mt-3 text-black/55 leading-relaxed">{p.body}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* SERVICES — grille symétrique, filets 1px */}
        {show("services") && services.length > 0 && (
          <section id="services" className="py-28 md:py-36 px-6 border-t border-black/10" data-testid="services-section">
            <div className="max-w-6xl mx-auto">
              <div className="max-w-2xl">
                <span className="text-xs uppercase tracking-[0.3em] text-black/40">{c.services.eyebrow}</span>
                <h2 className="tpl-head text-4xl md:text-5xl font-bold mt-5 leading-tight whitespace-pre-line">{c.services.title}</h2>
                <p className="mt-5 text-lg text-black/55">{c.services.lead}</p>
              </div>
              <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 border-l border-t border-black/10">
                {services.map((s, i) => (
                  <motion.a key={i} href={tenantHref("/reserver")} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} data-testid={`service-card-${i}`} className="group block border-r border-b border-black/10 p-9 hover:bg-black hover:text-white transition-colors duration-300">
                    <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-black/40 group-hover:text-white/50">
                      <span>{s.tag}</span><span>{s.duration}</span>
                    </div>
                    <h3 className="tpl-head text-2xl font-semibold mt-5">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-black/55 group-hover:text-white/70">{s.body}</p>
                    <div className="mt-6 inline-flex items-center gap-1 text-sm font-medium">{c.services.cta} <ArrowUpRight size={15} /></div>
                  </motion.a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* TESTIMONIALS — citation centrée */}
        {show("testimonials") && testimonials.length > 0 && (
          <section id="testimonials" className="py-32 px-6 border-t border-black/10 bg-[var(--tpl-secondary)]" data-testid="testimonials-section">
            <div className="max-w-3xl mx-auto text-center min-h-[220px] flex flex-col justify-center">
              <Quote size={32} className="mx-auto text-black/20" />
              <motion.p key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="tpl-head text-2xl md:text-4xl font-medium leading-snug mt-8" data-testid={`testimonial-${active}`}>
                {formatQuote(testimonials[active]?.quote)}
              </motion.p>
              <div className="mt-8 text-sm uppercase tracking-[0.25em] text-black/50">{testimonials[active]?.name}</div>
              <div className="mt-6 flex items-center justify-center gap-2">
                {testimonials.map((_, i) => <button key={i} onClick={() => setActive(i)} className={`h-1.5 rounded-full transition-all ${i === active ? "w-6 bg-black" : "w-1.5 bg-black/25"}`} aria-label={`avis ${i + 1}`} />)}
              </div>
            </div>
          </section>
        )}

        {/* BLOG */}
        {show("blog") && posts.length > 0 && (
          <section id="blog" className="py-28 md:py-36 px-6 border-t border-black/10" data-testid="blog-section">
            <div className="max-w-6xl mx-auto">
              <h2 className="tpl-head text-4xl md:text-5xl font-bold leading-tight whitespace-pre-line">{c.story.eyebrow && "Journal"}</h2>
              <BlogGrid
                posts={posts}
                gridClassName="mt-12 gap-px bg-black/10 border border-black/10"
                cardClassName="bg-[var(--tpl-bg)] p-8 group"
                renderCard={(p) => (
                  <>
                    <img src={p.cover} alt="" className="w-full h-44 object-cover mb-5" />
                    <div className="text-[11px] uppercase tracking-widest text-black/40">{p.tag} · {p.date}</div>
                    <h3 className="tpl-head text-xl font-semibold mt-2 group-hover:underline">{p.title}</h3>
                    <p className="mt-2 text-sm text-black/55 leading-relaxed">{p.excerpt}</p>
                  </>
                )}
              />
            </div>
          </section>
        )}

        {/* CONTACT */}
        {show("contact") && (
          <section id="contact" className="py-28 md:py-36 px-6 border-t border-black/10" data-testid="contact-section">
            <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-14">
              <div>
                <h2 className="tpl-head text-4xl md:text-5xl font-bold leading-tight whitespace-pre-line">{c.contact.title}</h2>
                <p className="mt-5 text-lg text-black/55">{c.contact.lead}</p>
                <div className="mt-10 space-y-4">
                  <a href={`mailto:${c.contact.info.email}`} className="flex items-center gap-3 text-lg hover:underline" data-testid="contact-email-link"><Mail size={18} /> {c.contact.info.email}</a>
                  <div className="flex items-center gap-3 text-lg"><MapPin size={18} /> {c.contact.info.place}</div>
                  <a href={tenantHref("/reserver")} className="inline-flex items-center gap-2 rounded-full border border-black/15 px-7 py-3.5 font-medium hover:border-black transition-colors mt-2">{c.nav.book} <ArrowRight size={16} /></a>
                </div>
              </div>
              <ContactForm classNames={FORM_CLASSES} icon={<ArrowRight size={16} />} />
            </div>
          </section>
        )}

        <MinimalFooter toggles={toggles} />
      </main>
    </TemplateShell>
  );
}
